// ============================================================================
// DocCard — a knowledge/plan document
// Created: doc glyph, title, description line, mono footer (doc_type ·
// updated). Updated/deleted: one-row receipt form with the change note.
// ============================================================================

import { Box, Typography } from '@mui/material'
import { Description } from '@mui/icons-material'
import { typography, spacing } from '@/theme/muiTheme'
import type { RenderPayload, DocSpec } from '@/types/chatRenders'
import CardShell, { cardChromeSx, monoMetaSx, StatusRing } from './CardShell'
import { statusColor, formatShortDate, entityRoute } from './cardUtils'

export default function DocCard({ render }: { render: RenderPayload }) {
  const spec = render.spec as unknown as Partial<DocSpec>
  const doc = spec.doc ?? {}
  const title = doc.title ?? render.entity.title
  const updated = formatShortDate(doc.updated_at)
  const event = spec.event ?? render.status
  const isCreated = event === 'created' || render.status === 'created'

  // Updated / deleted: one-row receipt form with the change note.
  if (!isCreated) {
    const deleted = event === 'deleted' || render.status === 'removed'
    return (
      <Box
        sx={{
          ...cardChromeSx,
          my: spacing.xs,
          px: spacing.md,
          py: spacing.sm,
          display: 'flex',
          alignItems: 'center',
          gap: spacing.sm,
          minWidth: 0,
        }}
      >
        <StatusRing color={statusColor(deleted ? 'removed' : render.status)} />
        <Typography
          noWrap
          sx={{
            fontSize: '14px',
            fontWeight: typography.fontWeightMedium,
            ...(deleted ? { color: 'text.secondary', textDecoration: 'line-through' } : {}),
          }}
        >
          {title}
        </Typography>
        <Typography noWrap sx={{ ...monoMetaSx, flex: 1, minWidth: 0 }}>
          {spec.change_note ?? (deleted ? 'Deleted' : 'Updated')}
        </Typography>
      </Box>
    )
  }

  // Preview: section headings + teaser lines (backend-derived). Falls back to the
  // one-line description when the render contract hasn't supplied sections yet.
  const sections = Array.isArray(doc.sections) ? doc.sections : []
  const route = entityRoute(render.entity)
  const footerParts = [
    doc.section_count != null ? `${doc.section_count} sections` : doc.doc_type,
    doc.word_count != null ? `${doc.word_count} words` : updated ? `Updated ${updated}` : null,
    'By Flow',
  ].filter(Boolean)

  const teaserSx = {
    fontSize: '12px',
    color: 'text.secondary',
    overflow: 'hidden',
    display: '-webkit-box',
    WebkitBoxOrient: 'vertical' as const,
  }

  return (
    <CardShell kindLabel="Doc" action={route ? { label: 'Open', to: route } : undefined} disableBodyPadding>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: spacing.sm, minWidth: 0, px: spacing.md, py: spacing.sm }}>
        <Description sx={{ fontSize: 18, color: 'text.secondary', mt: '1px', flexShrink: 0 }} />
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography noWrap sx={{ fontSize: '14px', fontWeight: typography.fontWeightSemibold }}>
            {title}
          </Typography>
          {sections.length > 0 ? (
            <Box sx={{ mt: '6px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {sections.map((s, i) => (
                <Box key={i} sx={{ minWidth: 0 }}>
                  <Typography sx={{ ...monoMetaSx, fontSize: '9px' }}>{s.heading}</Typography>
                  {s.teaser && (
                    <Typography sx={{ ...teaserSx, WebkitLineClamp: 1 }}>{s.teaser}</Typography>
                  )}
                </Box>
              ))}
            </Box>
          ) : doc.description ? (
            <Typography sx={{ ...teaserSx, WebkitLineClamp: 2 }}>{doc.description}</Typography>
          ) : null}
        </Box>
      </Box>
      {/* Mono footer under a full-bleed hairline (frames). */}
      <Typography
        noWrap
        sx={{
          ...monoMetaSx,
          display: 'block',
          px: spacing.md,
          py: '8px',
          borderTop: '1px solid rgba(248, 246, 244, 0.09)',
        }}
      >
        {footerParts.join(' · ')}
      </Typography>
    </CardShell>
  )
}
